import React, { useMemo } from 'react';
import { BeveledPanel } from '../FabProcessTool/BeveledPanel';
import {
  getGraphiteMetal,
  getRubberMaterial,
  getWhitePaintedMetal,
  getBrushedAluminum,
  getCyanLedMaterial,
} from '../FabProcessTool/equipmentMaterials';
import type { Awp300Layout } from './awp300Layout';

export type Awp300BodyProps = {
  layout: Awp300Layout;
  opacity?: number;
  ledIntensity?: number;
};

/** Tiered black base, white corner pillars with cyan LED strips, white top cap. */
export const Awp300Body: React.FC<Awp300BodyProps> = ({
  layout,
  opacity = 1,
  ledIntensity = 1,
}) => {
  const white = useMemo(() => getWhitePaintedMetal(opacity), [opacity]);
  const graphite = useMemo(() => getGraphiteMetal(opacity), [opacity]);
  const rubber = useMemo(() => getRubberMaterial(opacity), [opacity]);
  const brushed = useMemo(() => getBrushedAluminum(opacity), [opacity]);
  const cyan = useMemo(() => getCyanLedMaterial(ledIntensity, opacity), [ledIntensity, opacity]);

  const {
    width,
    depth,
    baseH,
    capH,
    pillarBottom,
    pillarTop,
    frameT,
    hx,
    hz,
    colW,
  } = layout;

  const colH = pillarTop - pillarBottom;
  const colY = (pillarTop + pillarBottom) * 0.5;
  const r = Math.min(width, depth) * 0.012;
  const tier1 = baseH * 0.38;
  const tier2 = baseH * 0.32;
  const deckH = baseH - tier1 - tier2;

  const corners = (
    [
      [-hx, -hz],
      [hx, -hz],
      [-hx, hz],
      [hx, hz],
    ] as const
  );

  return (
    <group name="Awp300Body">
      <BeveledPanel
        size={[width * 1.03, tier1, depth * 1.02]}
        material={rubber}
        position={[0, tier1 * 0.5, 0]}
        radius={r * 0.12}
        receiveShadow
      />
      <BeveledPanel
        size={[width * 1.0, tier2, depth * 0.98]}
        material={graphite}
        position={[0, tier1 + tier2 * 0.5, 0]}
        radius={r * 0.1}
        receiveShadow
      />
      <BeveledPanel
        size={[width * 0.94, Math.max(deckH, frameT * 0.4), depth * 0.92]}
        material={brushed}
        position={[0, baseH - deckH * 0.5, 0]}
        radius={r * 0.08}
        castShadow
        receiveShadow
      />

      {corners.map(([x, z], i) => {
        const isFront = z < 0;
        const side = Math.sign(x);
        return (
          <group key={`pillar-${i}`}>
            <BeveledPanel
              size={[colW, colH, colW * 1.06]}
              material={white}
              position={[x, colY, z]}
              radius={r * 0.2}
              smoothness={2}
              castShadow
            />
            <BeveledPanel
              size={[colW * 1.08, colH, frameT * 0.55]}
              material={graphite}
              position={[x, colY, z + (isFront ? -frameT * 0.22 : frameT * 0.18)]}
              radius={r * 0.15}
            />
            {isFront ? (
              <BeveledPanel
                size={[colW * 0.1, colH * 0.94, frameT * 0.38]}
                material={cyan}
                position={[x + side * colW * 0.5, colY, z - frameT * 0.15]}
                smoothness={2}
              />
            ) : null}
          </group>
        );
      })}

      <BeveledPanel
        size={[width * 0.97, frameT, depth * 0.96]}
        material={graphite}
        position={[0, pillarBottom + frameT * 0.55, 0]}
        radius={r * 0.15}
      />
      <BeveledPanel
        size={[width * 0.98, capH * 0.46, depth * 0.96]}
        material={white}
        position={[0, pillarTop + capH * 0.23, 0]}
        radius={r * 0.3}
        smoothness={3}
        castShadow
      />
      <BeveledPanel
        size={[width * 0.94, capH * 0.34, depth * 0.92]}
        material={brushed}
        position={[0, pillarTop + capH * 0.58, 0]}
        radius={r * 0.25}
        smoothness={3}
        castShadow
      />
    </group>
  );
};

export default Awp300Body;
